import { ref, computed, onMounted, watch } from 'vue'
import { MOPDataTable } from '@/components/primevuecomponents/data/MOPDataTable/MOPDataTable'
import type { MOPDataTableColumn } from '@/components/primevuecomponents/data/MOPDataTable/MOPDataTable'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../../stores/auth'
import { moduleService, type Module } from '../../services/moduleService'
import { useToast } from 'primevue/usetoast'
import moment from 'moment'
import MastersService from '../../../../MoceroUI/src/modules/Inventory/Service/MastersService'
import StoreService from '../../../../MoceroUI/src/modules/Inventory/Service/StoreService'
import {
  getOrgCirculars, getDeptCirculars, getDepartmentsByOrg, getSocialPageConfigs,
  getSOPDocuments, getSOPDocumentsByCategory, searchSOPDocuments,
  getMyTasks, getDeptTasks, getUpcomingBirthdays,
  registerDeviceToken, unregisterDeviceToken, getDashboardUserContext, getNewsByOrg
} from '../../services/dashboardService'
import { moduleService as modSvc } from '../../services/moduleService'

export function useDashboardData() {
  const router = useRouter()
  const authStore = useAuthStore()
  const toast = useToast()

  // Core
  const loading = ref(false)
  const error = ref<string | null>(null)
  const modules = ref<Module[]>([])

  // News
  const news = ref<any[]>([])
  const selectedNewsCategory = ref('All')
  const newsCategories = ['All', 'General', 'Clinical', 'HR', 'Operations', 'Emergency', 'Training', 'Policy']

  // Circulars
  const orgCirculars = ref<any[]>([])
  const deptCirculars = ref<any[]>([])
  const deptOptions = ref<{ label: string; value: number }[]>([])
  const selectedDeptId = ref<number | null>(null)

  // Admin
  const isAdmin = computed(() => authStore.isAdmin || authStore.user?.isAdmin || false)

  // Social
  const socialPages = ref<any[]>([])
  const activeSocialTab = ref(0)

  // Birthdays
  const birthdays = ref<any[]>([])

  // Tasks
  const activeTaskTab = ref<'my' | 'dept'>('my')
  const tasksLoading = ref(false)
  const myTasks = ref<any[]>([])
  const deptTasksList = ref<any[]>([])
  const currentTasks = computed(() => activeTaskTab.value === 'my' ? myTasks.value : deptTasksList.value)

  // SOPs
  const allSOPs = ref<any[]>([])
  const filteredSOPs = ref<any[]>([])
  const sopLoading = ref(false)
  const sopSearchTerm = ref('')
  const selectedCategory = ref('All')
  const sopCategories = ['All', 'Clinical', 'HR', 'Pharmacy', 'Emergency', 'Admin', 'Compliance']

  // Misc
  const unreadCount = ref(0)
  let filteredMenus: any = ref([])
  let menuarray: any = ref([])
  let storeData = ref({
    status: '',
    Id: 0,
    Type: 'StoreId',
    storename: '',
    storebranchmapid: 0,
    branchid: 0,
    IsZeroBill: false,
    IsCamp: false,
    SiteId: 0
  })

  // Computed
  const accessibleModules = computed(() => modules.value.filter(m => m.isAccessible))
  const toBePurchasedModules = computed(() => modules.value.filter(m => !m.isAccessible))

  const tasksDueToday = computed(() =>
    myTasks.value.filter(t => {
      if (!t.EndDate || isTaskCompleted(t)) return false
      const due = moment(t.EndDate, ['DD-MM-YYYY', moment.ISO_8601], true)
      return due.isValid() && due.isSame(moment(), 'day')
    })
  )

  const sopCount = computed(() => allSOPs.value.length)

  const taskStats = computed(() => {
    const tasks = myTasks.value
    const pending = tasks.filter(t => !isTaskCompleted(t)).length
    const inProgress = tasks.filter(t => (t.TaskStatusName || '').toLowerCase().includes('progress')).length
    const overdue = tasks.filter(t => isPastDue(t.EndDate) && !isTaskCompleted(t)).length
    return { pending, inProgress, overdue, total: tasks.length }
  })

  const sortedTasks = computed(() => {
    const priorityOrder = (name: string) => {
      const n = (name || '').toLowerCase()
      if (n === 'high' || n === 'critical' || n === 'urgent') return 0
      if (n === 'medium' || n === 'normal') return 1
      return 2
    }
    return [...currentTasks.value].sort((a, b) => {
      const aOver = isPastDue(a.EndDate) && !isTaskCompleted(a)
      const bOver = isPastDue(b.EndDate) && !isTaskCompleted(b)
      if (aOver !== bOver) return aOver ? -1 : 1
      const aPri = priorityOrder(a.TaskPriorityName)
      const bPri = priorityOrder(b.TaskPriorityName)
      if (aPri !== bPri) return aPri - bPri
      const aComp = isTaskCompleted(a) ? 1 : 0
      const bComp = isTaskCompleted(b) ? 1 : 0
      if (aComp !== bComp) return aComp - bComp
      if (a.EndDate && b.EndDate) {
        return moment(a.EndDate, ['DD-MM-YYYY', moment.ISO_8601]).valueOf()
             - moment(b.EndDate, ['DD-MM-YYYY', moment.ISO_8601]).valueOf()
      }
      return 0
    })
  })

  const taskColumns: MOPDataTableColumn[] = [
    { field: 'TaskPriorityName', header: 'Priority', sortable: true, width: '90px' },
    { field: 'TaskName', header: 'Task', sortable: true },
    { field: 'DepartmentName', header: 'Department', sortable: true, width: '140px' },
    { field: 'TaskStatusName', header: 'Status', sortable: true, width: '110px' },
    { field: 'EndDate', header: 'Due / Remaining', sortable: true, width: '140px' }
  ]

  // Functions
  async function loadModules() {
    try {
      error.value = null
      loading.value = true
      const orgId = authStore.user?.organizationId
      if (!orgId) throw new Error('Organization ID not found.')
      modules.value = await moduleService.getUserModules(orgId)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load modules'
    } finally {
      loading.value = false
    }
  }

  async function loadNews(category?: string) {
    try {
      const ctx = getDashboardUserContext()
      if (ctx.orgId) {
        news.value = await getNewsByOrg(ctx.orgId, ctx.branchId, ctx.roleId, category)
      } else {
        const response = await moduleService.loadNews()
        news.value = response?.result ?? []
      }
    } catch { news.value = [] }
  }

  async function selectNewsCategory(cat: string) {
    selectedNewsCategory.value = cat
    await loadNews(cat === 'All' ? undefined : cat)
  }

  async function loadDashboardData() {
    const ctx = getDashboardUserContext()
    if (!ctx.orgId) return
    await Promise.allSettled([
      loadCirculars(ctx),
      loadSocialPages(ctx),
      loadBirthdays(ctx),
      loadMyTasks(ctx),
      loadSOPs(ctx)
    ])
  }

  async function loadCirculars(ctx: ReturnType<typeof getDashboardUserContext>) {
    if (ctx.isAdmin && deptOptions.value.length === 0) {
      const depts = await getDepartmentsByOrg(ctx.orgId)
      deptOptions.value = depts.map((d: any) => ({ label: d.Name, value: d.ID }))
    }
    const deptId = ctx.isAdmin ? selectedDeptId.value : ctx.departmentId
    const [org, dept] = await Promise.allSettled([
      getOrgCirculars(ctx.orgId, ctx.branchId, ctx.isAdmin),
      deptId ? getDeptCirculars(ctx.orgId, ctx.branchId, deptId, ctx.isAdmin) : Promise.resolve([])
    ])
    orgCirculars.value = org.status === 'fulfilled' ? (org.value ?? []) : []
    deptCirculars.value = dept.status === 'fulfilled' ? (dept.value ?? []) : []
  }

  watch(selectedDeptId, async () => {
    const ctx = getDashboardUserContext()
    const deptId = selectedDeptId.value
    deptCirculars.value = deptId ? (await getDeptCirculars(ctx.orgId, ctx.branchId, deptId, true)) ?? [] : []
  })

  async function loadSocialPages(ctx: ReturnType<typeof getDashboardUserContext>) {
    socialPages.value = await getSocialPageConfigs(ctx.orgId, ctx.branchId)
  }

  async function loadBirthdays(ctx: ReturnType<typeof getDashboardUserContext>) {
    birthdays.value = await getUpcomingBirthdays(ctx.orgId, 30)
  }

  async function loadMyTasks(ctx: ReturnType<typeof getDashboardUserContext>) {
    tasksLoading.value = true
    try {
      const result = await getMyTasks(ctx.orgId, ctx.branchId, ctx.userId, ctx.isAdmin, ctx.deptRoleMapId)
      if (Array.isArray(result)) myTasks.value = result
    } finally {
      tasksLoading.value = false
    }
  }

  async function loadDeptTasks(ctx: ReturnType<typeof getDashboardUserContext>) {
    tasksLoading.value = true
    try {
      const result = await getDeptTasks(ctx.orgId, ctx.branchId, ctx.departmentId, ctx.userId, ctx.isAdmin, ctx.deptRoleMapId)
      if (Array.isArray(result)) deptTasksList.value = result
    } finally {
      tasksLoading.value = false
    }
  }

  async function loadSOPs(ctx: ReturnType<typeof getDashboardUserContext>) {
    sopLoading.value = true
    try {
      allSOPs.value = await getSOPDocuments(ctx.orgId, ctx.branchId)
      filteredSOPs.value = allSOPs.value
    } finally {
      sopLoading.value = false
    }
  }

  async function switchTaskTab(tab: 'my' | 'dept') {
    activeTaskTab.value = tab
    const ctx = getDashboardUserContext()
    if (tab === 'my') await loadMyTasks(ctx)
    else await loadDeptTasks(ctx)
  }

  let sopSearchTimer: ReturnType<typeof setTimeout> | null = null
  function onSOPSearch() {
    if (sopSearchTimer) clearTimeout(sopSearchTimer)
    sopSearchTimer = setTimeout(applySOPFilter, 400)
  }

  async function applySOPFilter() {
    const ctx = getDashboardUserContext()
    sopLoading.value = true
    try {
      if (sopSearchTerm.value.trim().length >= 2) {
        filteredSOPs.value = await searchSOPDocuments(ctx.orgId, ctx.branchId, sopSearchTerm.value.trim())
      } else if (selectedCategory.value && selectedCategory.value !== 'All') {
        filteredSOPs.value = await getSOPDocumentsByCategory(ctx.orgId, ctx.branchId, selectedCategory.value)
      } else {
        filteredSOPs.value = allSOPs.value
      }
    } finally {
      sopLoading.value = false
    }
  }

  function clearSOPSearch() {
    sopSearchTerm.value = ''
    applySOPFilter()
  }

  async function selectCategory(cat: string) {
    selectedCategory.value = cat
    sopSearchTerm.value = ''
    await applySOPFilter()
  }

  async function registerFCMToken() {
    try {
      if (!('Notification' in window) || !('serviceWorker' in navigator)) return
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') return
      const ctx = getDashboardUserContext()
      if (ctx.userId && ctx.orgId) {
        const webToken = localStorage.getItem('fcmWebToken')
        if (webToken) {
          await registerDeviceToken({
            UserId: ctx.userId,
            DeviceToken: webToken,
            Platform: 'web',
            OrganizationId: ctx.orgId,
            BranchId: ctx.branchId,
            CreatedBy: ctx.userId
          })
        }
      }
    } catch { }
  }

  async function handleModuleSelect(module: Module | undefined) {
    if (!module) return
    try {
      if (!module.isAccessible) return
      loading.value = true
      const roleId = authStore.user?.roleId
      if (!roleId) throw new Error('Role ID not found')
      if (!module.id || typeof module.id !== 'number') throw new Error('Invalid module ID')
      authStore.setActiveModule({ id: module.id, name: module.name, rights: [], menu: { menus: [], subMenus: [] } })
      if (module.type === 'moceroui') {
        await handleMoceroUIModule(module)
      } else {
        await handleMOPUIModule(module, roleId)
      }
      toast.add({ severity: 'success', summary: 'Success', detail: `Switched to ${module.name}`, life: 3000 })
    } catch (err) {
      toast.add({ severity: 'error', summary: 'Error', detail: err instanceof Error ? err.message : 'Failed', life: 3000 })
    } finally {
      loading.value = false
    }
  }

  async function handleMoceroUIModule(module: Module) {
    module.Path = await LoadMenus(
      authStore.user?.roleId,
      authStore.user?.organizationId,
      authStore.user?.id,
      authStore.user?.admin,
      authStore.user?.branchId
    )
    await moduleService.prepareSessionForMoceroUI(module)
    moduleService.navigateToMoceroUI(module)
  }

  async function handleMOPUIModule(module: Module, roleId: number) {
    const rights = await moduleService.getModuleRights(roleId, module.id)
    authStore.setActiveModule({ id: module.id, name: module.name, rights, menu: authStore.activeModuleMenu })
    if (module.id === 10) {
      await router.push({ path: '/Task' })
    } else {
      await router.push({ path: '/MOPLayout', query: { moduleId: module.id.toString() } })
    }
  }

  function getModuleByName(name: string): Module | undefined {
    return modules.value.find(m => m.name.toLowerCase().includes(name.toLowerCase()))
  }

  const handleLogout = async () => {
    try {
      const ctx = getDashboardUserContext()
      if (ctx.userId) await unregisterDeviceToken(ctx.userId, 'web', ctx.userId)
      localStorage.removeItem('token')
      localStorage.removeItem('RolesData')
      localStorage.removeItem('UsersData')
      authStore.clearAuth()
      await router.push('/')
    } catch (e) { console.error(e) }
  }

  async function LoadMenus(RoleId: any, OrganizationId: any, UserId: any, Admin: any, BranchId: any) {
    let response: any = await MastersService.GetMenus(1, RoleId, OrganizationId)
    menuarray.value = response['result']
    const uniqueMenus = new Map()
    response['result'].forEach((item: any) => {
      const { MenuId, MenuName, MenuPath } = item
      if (!uniqueMenus.has(MenuId)) uniqueMenus.set(MenuId, { MenuName, MenuPath })
    })
    filteredMenus.value = Array.from(uniqueMenus, ([MenuId, { MenuName, MenuPath }]) => ({ MenuId, MenuName, MenuPath }))
    try {
      await LoadStore(authStore.user?.id, authStore.user?.organizationId, authStore.user?.admin, authStore.user?.branchId)
      let TabIndex = { Indexval: 0 }
      localStorage.setItem('TabIndex', JSON.stringify(TabIndex))
      localStorage.setItem('RequestSubTabIndex', JSON.stringify(TabIndex))
      return JSON.stringify({
        name: 'StoreTabDetails',
        query: {
          status: storeData.value.status,
          Id: storeData.value.Id,
          Type: 'StoreId',
          storename: storeData.value.storename,
          storebranchmapid: storeData.value.storebranchmapid,
          BranchId: storeData.value.branchid,
          IsZeroBill: String(storeData.value.IsZeroBill),
          IsCamp: String(storeData.value.IsCamp),
          SiteId: storeData.value.SiteId
        }
      })
    } catch { return '/Masters' }
  }

  async function LoadStore(UserId: any, OrganizationId: any, Admin: any, BranchId: any) {
    let result: any = await StoreService.StoreDropDownListByUserId(UserId, OrganizationId, Admin, BranchId)
    storeData.value.Id = result['result'][0]['Id']
    storeData.value.Type = result['result'][0]['Type']
    storeData.value.storebranchmapid = result['result'][0]['StoreBranchMapId']
    if (result['result'][0]['StoreTypeName'] === 'Main Store') storeData.value.status = '1'
    else if (result['result'][0]['StoreTypeName'] === 'Sub Store') storeData.value.status = '2'
    else storeData.value.status = '3'
    storeData.value.storename = result['result'][0]['Name']
    storeData.value.branchid = result['result'][0]['BranchId']
    storeData.value.IsZeroBill = result['result'][0]['IsZeroBill'] || false
    storeData.value.IsCamp = result['result'][0]['IsCamp'] || false
    storeData.value.SiteId = result['result'][0]['SiteId'] || 0
  }

  function formatDate(d: any) {
    if (!d) return ''
    const m = moment(d, ['DD-MM-YYYY', moment.ISO_8601], true)
    return m.isValid() ? m.format('MMM DD, YYYY') : moment(d).format('MMM DD, YYYY')
  }

  function isPastDue(d: any) {
    if (!d) return false
    const m = moment(d, ['DD-MM-YYYY', moment.ISO_8601], true)
    return m.isValid() ? m.isBefore(moment(), 'day') : false
  }

  function isTaskCompleted(task: any) {
    const name = (task.TaskStatusName || task.Status || '').toLowerCase()
    return name.includes('complet') || name.includes('done') || name.includes('closed')
  }

  function timeRemaining(d: any) {
    if (!d) return '—'
    const due = moment(d, ['DD-MM-YYYY', moment.ISO_8601], true)
    if (!due.isValid()) return formatDate(d)
    const diff = due.startOf('day').diff(moment().startOf('day'), 'days')
    if (diff < 0) return `${Math.abs(diff)}d overdue`
    if (diff === 0) return 'Due today'
    if (diff === 1) return 'Tomorrow'
    return `${diff}d left`
  }

  function dueCellClass(d: any, statusName: string) {
    const name = (statusName || '').toLowerCase()
    if (name.includes('complet') || name.includes('done') || name.includes('closed')) return 'due-completed'
    const due = moment(d, ['DD-MM-YYYY', moment.ISO_8601], true)
    if (!due.isValid()) return ''
    const diff = due.startOf('day').diff(moment().startOf('day'), 'days')
    if (diff < 0) return 'due-overdue'
    if (diff <= 3) return 'due-soon'
    return 'due-ok'
  }

  function getInitials(name: string) {
    if (!name) return '?'
    return name.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()
  }

  function priorityClass(name: string) {
    const n = (name || '').toLowerCase()
    if (n === 'high' || n === 'critical' || n === 'urgent') return 'priority-high'
    if (n === 'medium' || n === 'normal') return 'priority-medium'
    return 'priority-low'
  }

  function statusClass(name: string) {
    const n = (name || '').toLowerCase()
    if (n.includes('complet') || n.includes('done') || n.includes('closed')) return 'status-completed'
    if (n.includes('progress') || n.includes('active') || n.includes('ongoing')) return 'status-inprogress'
    return 'status-pending'
  }

  function socialIcon(platform: string) {
    const map: Record<string, string> = {
      facebook: 'pi pi-facebook',
      youtube: 'pi pi-youtube',
      instagram: 'pi pi-instagram'
    }
    return map[platform?.toLowerCase()] || 'pi pi-globe'
  }

  function scrollTo(id: string) {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  onMounted(async () => {
    await loadModules()
    await loadNews()
    await loadDashboardData()
    await registerFCMToken()
  })

  return {
    // State
    loading,
    error,
    modules,
    news,
    selectedNewsCategory,
    newsCategories,
    orgCirculars,
    deptCirculars,
    deptOptions,
    selectedDeptId,
    isAdmin,
    socialPages,
    activeSocialTab,
    birthdays,
    activeTaskTab,
    tasksLoading,
    myTasks,
    deptTasksList,
    currentTasks,
    allSOPs,
    filteredSOPs,
    sopLoading,
    sopSearchTerm,
    selectedCategory,
    sopCategories,
    unreadCount,
    filteredMenus,
    menuarray,
    storeData,

    // Computed
    accessibleModules,
    toBePurchasedModules,
    tasksDueToday,
    sopCount,
    taskStats,
    sortedTasks,
    taskColumns,

    // Functions
    loadModules,
    loadNews,
    selectNewsCategory,
    loadDashboardData,
    loadCirculars,
    loadSocialPages,
    loadBirthdays,
    loadMyTasks,
    loadDeptTasks,
    loadSOPs,
    switchTaskTab,
    onSOPSearch,
    applySOPFilter,
    clearSOPSearch,
    selectCategory,
    registerFCMToken,
    handleModuleSelect,
    handleMoceroUIModule,
    handleMOPUIModule,
    getModuleByName,
    handleLogout,
    LoadMenus,
    LoadStore,
    formatDate,
    isPastDue,
    isTaskCompleted,
    timeRemaining,
    dueCellClass,
    getInitials,
    priorityClass,
    statusClass,
    socialIcon,
    scrollTo
  }
}
